import React, { useContext, useEffect, useRef, useState } from "react";
import Loader from "react-loader-spinner";
import { NotificationManager } from "react-notifications";
import swal from "sweetalert";
import * as copy from "copy-to-clipboard";
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  TelegramShareButton,
  TelegramIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share";
import { httpGet } from "../../components/helpers/httpMethods";
import { UserDataContext } from "../../context/userContext";
import { CartContextData } from "../../context/newsPaperContext";
import "./publicationPage.css";
import SEO from "../Layout/SEO";
import BannerSlot from "../Banner/BannerSlot";
import FollowButton from "../Follow/FollowButton";
import { openFree } from "../Pay/readFree";
import { notPublishing } from "../helpers/publishing";

// Search titles: "VANGUARD" -> "Vanguard"; clean address /publication/vanguard (same rule as the server sitemap).
const niceName = (n) => String(n || "").trim().toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (m, a, b) => a + b.toUpperCase());
const slugOf = (n) => String(n || "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();

/*
 * Redesigned publication page (/publication/:id).
 * Behaviour is kept identical to publicEditions.jsx: same endpoint, same
 * read/subscribe/login flow, same checkout modal (onOpenModal), same share links.
 */

// Share links keep pointing at the current production domain, as before.
const SHARE_BASE = "https://app.thenewspaperstand.com";

const PLANS = [
  { key: "weeklyFee", name: "Weekly", note: "per week" },
  { key: "monthlyFee", name: "Monthly", note: "per month" },
  { key: "quarterlyFee", name: "Quarterly", note: "every 3 months" },
  { key: "biannuallyFee", name: "6 months", note: "every 6 months" },
  { key: "annualFee", name: "Annual", note: "per year" },
];

const hasPrice = (v) => v !== null && v !== undefined && v !== "" && Number(v) > 0;
const naira = (v) => `₦${Number(v).toLocaleString("en-NG")}`;

const fmt = (d, opts) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", opts);
  } catch (e) {
    return "";
  }
};
const shortDate = (d) => fmt(d, { weekday: "short", day: "numeric", month: "short" });
const longDate = (d) => fmt(d, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const isToday = (d) => new Date(d).toDateString() === new Date().toDateString();

export default function PublicationPage(props) {
  const { userIsLogin, checkUserIsLogin } = useContext(UserDataContext);
  const { onOpenModal, mySubs } = useContext(CartContextData);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [publication, setPublication] = useState(null);
  const [editions, setEditions] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const shareRef = useRef(null);

  const slug = props.match.params.id;
  // Share by name (works on both sites), even when the page was opened by id.
  const shareSlug = publication && publication.name ? encodeURIComponent(publication.name.trim()) : slug;
  const shareUrl = `${SHARE_BASE}/publication/${shareSlug}/`;

  const load = async () => {
    setLoading(true);
    setNotFound(false);
    const res = await httpGet(`single_publication/${slug}`);
    if (!res || res.er) {
      if (res && res.er) {
        NotificationManager.error(res.er.message || String(res.er));
        if (res.er.message === "You do not have an active subscription with this publication") {
          props.history.goBack();
        }
      }
      setNotFound(true);
      setLoading(false);
      return;
    }
    setPublication(res.data.findPublication);
    setEditions(res.data.editions || []);
    setHighlights([]);
    const first = (res.data.editions || [])[0];
    if (first) {
      httpGet(`edition_highlights/${first.id}`).then((r) => {
        if (r && r.code === 200 && r.data && Array.isArray(r.data.highlights)) setHighlights(r.data.highlights);
      });
    }
    setLoading(false);

    if (localStorage.getItem("paidWithWallet") === "true") {
      swal(
        "Wallet payment successful!",
        "Successfully paid for a publication using your wallet, always fund wallet to always perform wallet payment option.",
        "success"
      ).then(() => {
        localStorage.setItem("paidWithWallet", "false");
      });
    }
  };

  useEffect(() => {
    load();
    checkUserIsLogin();
  }, [slug]);

  // Close the share menu on outside click.
  useEffect(() => {
    if (!shareOpen) return;
    const onDown = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [shareOpen]);

  const goToLogin = () => {
    localStorage.setItem("comingFromPublicPublication", "true");
    localStorage.setItem("rediretURL", slug);
    props.history.push("/login");
  };

  // Same flow as the previous page's handleClickCheckSingle, plus (2026-10-29): always-free papers open straight away,
  // and a paid paper that has stopped publishing can't be bought here (its old issues go through the archive).
  const openEdition = (edition) => {
    if (!edition) return;
    if (userIsLogin !== true) return goToLogin();
    if (Array.isArray(mySubs) && mySubs.includes(edition.publicationId)) return props.history.push(`/view_single_paper/${edition.id}`);
    if (publication && publication.isPaid === false) return openFree({ publicationId: publication.id, editionId: edition.id, history: props.history });
    if (publication && editions[0] && notPublishing(publication, editions[0].publishedDate)) return props.history.push(`/archive?paper=${publication.id}`);
    return onOpenModal(edition, "fromPublicPublication");
  };

  const copyLink = () => {
    copy(shareUrl, { debug: true, message: "Press #{key} to copy" });
    NotificationManager.success(`Link copied to clipboard ${shareUrl}.`);
    setShareOpen(false);
  };

  if (loading) {
    return (
      <div className="row h-100 justify-content-center align-items-center app-spinner">
        <Loader type="TailSpin" color="#EF6C00" height={50} width={50} />
      </div>
    );
  }

  if (notFound || !publication) {
    return (
      <div className="pp-page">
        <div className="pp-empty">
          <h1 className="pp-empty-title">We couldn’t find this publication</h1>
          <p>It may have been renamed or removed.</p>
          <button type="button" className="pp-btn pp-btn-primary" onClick={() => props.history.push("/")}>
            Back to all papers
          </button>
        </div>
      </div>
    );
  }

  const name = (publication.name || "").trim();
  const latest = editions[0];
  const others = editions.slice(1);
  const subscribed = Array.isArray(mySubs) && mySubs.includes(publication.id);
  const freeUntil =
    publication.freeEndDate && new Date(publication.freeEndDate) > new Date() ? publication.freeEndDate : null;
  const free = publication.isPaid === false || Boolean(freeUntil);
  const plans = PLANS.filter((p) => hasPrice(publication[p.key]));
  const singlePrice = hasPrice(publication.oneOffFee) ? publication.oneOffFee : null;
  const fromPlan = plans.find((p) => p.key === "monthlyFee") || plans[0];
  const canRead = subscribed || free;
  const quiet = Boolean(latest) && !canRead && notPublishing(publication, latest.publishedDate); // stopped publishing: nothing to buy here

  const seoName = niceName(name);
  const seoDesc = `Read today’s ${seoName} as a full e-paper on TheNewspaperStand${singlePrice ? `, from ₦${Number(singlePrice).toLocaleString("en-NG")} a copy` : ""}${fromPlan ? ` or by subscription` : ""}. Old ${seoName} issues by date are in TheNewspaperStand’s archive.`;
  return (
    <div className="pp-page">
      <SEO
        title={`${seoName} Today: Read the ${seoName} E-paper Online | TheNewspaperStand`}
        description={seoDesc}
        url={`https://newstand.tech/publication/${slugOf(name)}`}
        image={latest && /^https?:/.test(latest.image || "") ? latest.image : undefined}
      />
      <nav className="pp-crumbs" aria-label="Breadcrumb">
        <button type="button" className="pp-link-quiet" onClick={() => props.history.push("/")}>
          All papers
        </button>
        <span aria-hidden="true">/</span>
        <span>{name}</span>
      </nav>

      <section className="pp-card pp-header">
        <div className="pp-logo">
          {publication.logo ? <img src={publication.logo} alt="" /> : <span>{name.charAt(0)}</span>}
        </div>
        <div className="pp-header-text">
          <h1 className="pp-title">{name}</h1>
          {publication.description ? <p className="pp-desc">{publication.description}</p> : null}
          {subscribed ? <span className="pp-chip pp-chip-blue">You’re subscribed</span> : null}
          {!subscribed && free ? (
            <span className="pp-chip pp-chip-green">
              {freeUntil ? `Free to read until ${shortDate(freeUntil)}` : "Free to read"}
            </span>
          ) : null}
        </div>
        <div className="pp-header-actions">
          <FollowButton big id={publication.id} name={publication.name} signedIn={userIsLogin === true} onSignIn={goToLogin} />
          <div className="pp-share" ref={shareRef}>
            <button
              type="button"
              className="pp-btn pp-btn-outline"
              aria-expanded={shareOpen}
              aria-haspopup="true"
              onClick={() => setShareOpen((v) => !v)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></svg>
              Share
            </button>
            {shareOpen ? (
              <div className="pp-share-menu">
                <FacebookShareButton url={shareUrl}><FacebookIcon size={36} round /></FacebookShareButton>
                <TwitterShareButton url={shareUrl}><TwitterIcon size={36} round /></TwitterShareButton>
                <LinkedinShareButton url={shareUrl}><LinkedinIcon size={36} round /></LinkedinShareButton>
                <TelegramShareButton url={shareUrl}><TelegramIcon size={36} round /></TelegramShareButton>
                <WhatsappShareButton url={shareUrl}><WhatsappIcon size={36} round /></WhatsappShareButton>
                <EmailShareButton url={shareUrl}><EmailIcon size={36} round /></EmailShareButton>
                <button type="button" className="pp-copy" onClick={copyLink}>Copy link</button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <div className="pp-main">
        <section className="pp-card pp-hero">
          {latest ? (
            <>
              <button type="button" className="pp-hero-cover" onClick={() => openEdition(latest)} aria-label={`Open the ${longDate(latest.publishedDate)} edition`}>
                <img src={latest.image} alt={`${name} front page, ${longDate(latest.publishedDate)}`} />
              </button>
              <div className="pp-hero-body">
                <div className="pp-eyebrow-row">
                  <span className="pp-eyebrow">{isToday(latest.publishedDate) ? "Today’s edition" : "Latest edition"}</span>
                  {isToday(latest.publishedDate) ? <span className="pp-chip pp-chip-green">New</span> : null}
                </div>
                <h2 className="pp-hero-date">{longDate(latest.publishedDate)}</h2>
                {latest.editionName ? <p className="pp-muted">{latest.editionName}</p> : null}
                {highlights.length > 0 ? (
                  <div className="pp-inside">
                    <span className="pp-inside-label">Inside this edition</span>
                    <ul className="pp-inside-list">
                      {highlights.map((h) => (
                        <li key={`${h.page}-${h.headline}`}>
                          <button
                            type="button"
                            className="pp-inside-item"
                            onClick={() =>
                              subscribed
                                ? props.history.push(`/view_single_paper/${latest.id}?page=${h.page}`)
                                : openEdition(latest)
                            }
                          >
                            <span className="pp-inside-headline">{h.headline}</span>
                            <span className="pp-inside-page">p. {h.page}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="pp-hero-actions">
                  {canRead ? (
                    <button type="button" className="pp-btn pp-btn-primary pp-btn-lg" onClick={() => openEdition(latest)}>
                      Read this edition
                    </button>
                  ) : quiet ? (
                    <button type="button" className="pp-btn pp-btn-outline pp-btn-lg" onClick={() => props.history.push(`/archive?paper=${publication.id}`)}>
                      Look for old issues
                    </button>
                  ) : (
                    <>
                      <button type="button" className="pp-btn pp-btn-primary pp-btn-lg" onClick={() => openEdition(latest)}>
                        {singlePrice ? `Buy this edition · ${naira(singlePrice)}` : "Get this edition"}
                      </button>
                      {plans.length > 0 ? (
                        <button type="button" className="pp-btn pp-btn-outline pp-btn-lg" onClick={() => openEdition(latest)}>
                          Subscribe
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className="pp-muted">No editions have been published yet.</p>
          )}
        </section>

        {quiet ? (
          <aside className="pp-card pp-plans pp-quiet" aria-labelledby="pp-quiet-h">
            <div className="pp-accent" />
            <h3 id="pp-quiet-h" className="pp-plans-title">Not available right now</h3>
            <p className="pp-muted">
              New editions of {name} aren’t available on TheNewspaperStand at the moment.
            </p>
            <p className="pp-muted">Follow it and it will show first on your home page if new editions are added.</p>
            <FollowButton big id={publication.id} name={publication.name} signedIn={userIsLogin === true} onSignIn={goToLogin} />
            <button type="button" className="pp-btn pp-btn-outline pp-btn-lg pp-btn-block" onClick={() => props.history.push(`/archive?paper=${publication.id}`)}>
              Look for old issues in the archive
            </button>
          </aside>
        ) : null}

        {!canRead && !quiet && (plans.length > 0 || singlePrice) ? (
          <aside className="pp-card pp-plans">
            <div className="pp-accent" />
            <h3 className="pp-plans-title">{plans.length > 0 ? "Read every edition" : "Get your copy"}</h3>
            <p className="pp-muted">
              {plans.length > 0
                ? `Subscribe to ${name} and get each edition as soon as it’s published.`
                : `Buy ${name} to start reading.`}
            </p>
            <ul className="pp-plan-list">
              {plans.map((p) => (
                <li key={p.key} className="pp-plan">
                  <span className="pp-plan-name">
                    {p.name}
                    {p.key === "monthlyFee" ? <span className="pp-tag">Popular</span> : null}
                  </span>
                  <span className="pp-plan-price">
                    {naira(publication[p.key])}
                    <span className="pp-plan-note"> {p.note}</span>
                  </span>
                </li>
              ))}
              {singlePrice ? (
                <li className="pp-plan">
                  <span className="pp-plan-name">Single edition</span>
                  <span className="pp-plan-price">{naira(singlePrice)}</span>
                </li>
              ) : null}
            </ul>
            <button type="button" className="pp-btn pp-btn-primary pp-btn-lg pp-btn-block" onClick={() => openEdition(latest)} disabled={!latest}>
              {userIsLogin === true
                ? plans.length > 0 ? "Choose a plan" : "Buy now"
                : plans.length > 0 ? "Log in to subscribe" : "Log in to buy"}
            </button>
            <p className="pp-secure">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
              Secure payment by card or bank transfer
            </p>
          </aside>
        ) : null}
      </div>

      <BannerSlot spot="paper_below" paper={publication.id} inline />

      {others.length > 0 ? (
        <section className="pp-recent">
          <h2 className="pp-section-title">Recent editions</h2>
          <div className="pp-grid">
            {others.map((ed) => (
              <button type="button" key={ed.id} className="pp-ed" onClick={() => openEdition(ed)}>
                <span className="pp-ed-cover">
                  <img src={ed.image} alt={`${name} front page, ${longDate(ed.publishedDate)}`} loading="lazy" />
                </span>
                <span className="pp-ed-row">
                  <span className="pp-ed-date">{shortDate(ed.publishedDate)}</span>
                  <span className="pp-ed-cta">{canRead ? "Read →" : quiet ? "Old issue →" : "Open →"}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {!canRead && fromPlan ? (
        <div className="pp-mobile-bar">
          <div className="pp-mobile-bar-text">
            <strong>From {naira(publication[fromPlan.key])}</strong>
            <span>{fromPlan.note}</span>
          </div>
          <button type="button" className="pp-btn pp-btn-primary" onClick={() => openEdition(latest)} disabled={!latest}>
            Subscribe
          </button>
        </div>
      ) : null}
    </div>
  );
}
